import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import useSelectedParty from '@/features/analysis/hooks/useSelectedParty';
import useEmailClient from '@/features/email/hooks/useEmailClient';
import useBroker from '@/hooks/useBroker';
import { getPartyName } from '@/lib/party';
import { isOlderThan12Months } from '@/lib/utils/date';
import hasItems from '@/lib/utils/hasItems';
import useGenerateAdvisoryPdfLink from '@/mutations/useGenerateAdvisoryPdfLink';

import { ALL_FLOW_IDS, QUICK_FLOW_IDS } from '../constants';
import useAdvisoryReports from '../queries/hooks/useAdvisoryReports';
import useConversations from '../queries/hooks/useConversations';
import useSystemCampaigns from '../queries/hooks/useSystemCampaigns';

export default function useRiskScan() {
  const { t } = useTranslation();

  const [advisoryReportPdfLink, setAdvisoryReportPdfLink] = useState<
    string | undefined
  >(undefined);

  const { broker } = useBroker();

  const { openSendMail } = useEmailClient();

  const party = useSelectedParty();

  const { mutateAsync: generateAdvisoryPdfLink } = useGenerateAdvisoryPdfLink();

  const { data: advisoryReports, isLoading: isAdvisoryReportLoading } =
    useAdvisoryReports(
      party?.id ?? '',
      {
        orderBy: 'modified_at',
      },
      {
        enabled: Boolean(party?.id),
      },
    );

  const { data: conversations, isLoading: isConversationsLoading } =
    useConversations(
      party?.id ?? '',
      {
        flowIds: ALL_FLOW_IDS,
        status: ['COMPLETED'],
        orderBy: 'modified_at',
      },
      { enabled: Boolean(party?.id) },
    );

  const { data: systemCampaigns } = useSystemCampaigns(party?.id ?? '', {
    enabled: Boolean(party?.id),
  });

  const latestScan = conversations?.[0];
  const isScanOutdated = isOlderThan12Months(latestScan?.modified_at);
  const latestAdvisoryReport = advisoryReports?.[0];

  const downloadLatestAdvisoryPdf = async () => {
    try {
      if (!latestAdvisoryReport?.id) throw new Error('No advisory reports');

      const result = await generateAdvisoryPdfLink(latestAdvisoryReport.id);

      setAdvisoryReportPdfLink(result.link);
    } catch {
      toast.error(t('PDF_LINK_FETCH_ERROR'));
    }
  };

  const handleSendQuickScan = () => {
    const campaign = systemCampaigns?.find((campaign) =>
      campaign.template.key.includes('QUICKSCAN'),
    );

    if (!campaign) return;

    const subject = t('RISKSCAN_EMAIL_SUBJECT_QUICK');
    const body = t('RISKSCAN_EMAIL_BODY_QUICK', {
      name: getPartyName(party),
      link: campaign.url,
      brokerName: broker?.name ?? t('RISKSCAN_EMAIL_DEFAULT_SENDER_NAME'),
      linkLabel: t('RISKSCAN_EMAIL_LINK_LABEL_QUICK'),
    });

    openSendMail({
      to: [party?.email ?? ''],
      subject,
      body,
    });
  };

  const handleSendRiskAnalysis = () => {
    const campaign = systemCampaigns?.find((campaign) =>
      campaign.template.key.includes('INTAKE'),
    );

    if (!campaign) return;

    const subject = t('RISKSCAN_EMAIL_SUBJECT_FULL');
    const body = t('RISKSCAN_EMAIL_BODY_FULL', {
      name: getPartyName(party),
      link: campaign.url,
      brokerName: broker?.name ?? t('RISKSCAN_EMAIL_DEFAULT_SENDER_NAME'),
      linkLabel: t('RISKSCAN_EMAIL_LINK_LABEL_FULL'),
    });

    openSendMail({
      to: [party?.email ?? ''],
      subject,
      body,
    });
  };

  return {
    hasAdvisoryReport: hasItems(advisoryReports),
    isAdvisoryReportLoading,
    latestAdvisoryReport,

    hasInventoryScan: hasItems(conversations),
    isInventoryScanLoading: isConversationsLoading,
    latestScan: conversations?.[0],
    latestQuickScan: conversations?.filter((scan) =>
      QUICK_FLOW_IDS.includes(scan.flow_id ?? ''),
    )?.[0],
    isScanOutdated,
    downloadLatestAdvisoryPdf,
    advisoryReportPdfLink,
    handleSendQuickScan,
    handleSendRiskAnalysis,
  };
}
